import { useEffect, useRef } from "react";
import type { Map as LeafletMap } from "leaflet";
import { CITY_COORDINATES, HQ_COORDINATES } from "@/data/city-coordinates";

const COPPER = "#C4841D";
const COPPER_LIGHT = "#D4A574";
const COVERAGE_RADIUS_METERS = 32000; // ~20 miles

const ServiceAreaMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    let cancelled = false;

    (async () => {
      // Dynamic imports so this only runs in the browser — Leaflet touches
      // `window` at module load and breaks SSR/prerender otherwise.
      const [{ default: L }, markerIcon2x, markerIcon, markerShadow] = await Promise.all([
        import("leaflet"),
        import("leaflet/dist/images/marker-icon-2x.png"),
        import("leaflet/dist/images/marker-icon.png"),
        import("leaflet/dist/images/marker-shadow.png"),
      ]);
      await import("leaflet/dist/leaflet.css");

      if (cancelled || !mapRef.current || mapInstanceRef.current) return;

      // Fix Leaflet default icon path issue with bundlers
      delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: markerIcon2x.default,
        iconUrl: markerIcon.default,
        shadowUrl: markerShadow.default,
      });

      const map = L.map(mapRef.current, {
        center: [HQ_COORDINATES.lat, HQ_COORDINATES.lng],
        zoom: 10,
        scrollWheelZoom: false,
      });
      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      // ~20-mile coverage radius
      L.circle([HQ_COORDINATES.lat, HQ_COORDINATES.lng], {
        radius: COVERAGE_RADIUS_METERS,
        color: COPPER,
        fillColor: COPPER_LIGHT,
        fillOpacity: 0.08,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      // HQ marker
      const hqIcon = L.divIcon({
        html: `<div style="background:${COPPER};width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>`,
        className: "",
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      L.marker([HQ_COORDINATES.lat, HQ_COORDINATES.lng], { icon: hqIcon })
        .addTo(map)
        .bindPopup(
          `<strong>Tru American Plumbing</strong><br/>Headquarters — Tolleson, AZ<br/><a href="tel:+14803222567">(480) 322-2567</a>`
        );

      // City circles
      CITY_COORDINATES.forEach((city) => {
        if (city.name === "Tolleson") return; // HQ already shown

        const circle = L.circle([city.lat, city.lng], {
          radius: 3200,
          color: COPPER,
          fillColor: COPPER,
          fillOpacity: 0.2,
          weight: 2,
        }).addTo(map);

        circle.bindTooltip(city.name, { direction: "top", offset: [0, -10] });

        const popupContent = city.slug
          ? `<strong>${city.name}, AZ</strong><br/><a href="/service-areas/${city.slug}">View city page →</a>`
          : `<strong>${city.name}, AZ</strong><br/>Service available`;

        circle.bindPopup(popupContent);
      });
    })();

    return () => {
      cancelled = true;
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  return (
    <div className="rounded-xl overflow-hidden border border-border shadow-sm">
      <div ref={mapRef} className="h-[400px] md:h-[500px] w-full bg-muted" />
      <div className="flex items-center gap-6 px-4 py-3 bg-muted/50 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: COPPER }} />
          Service Area
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-dashed" style={{ borderColor: COPPER }} />
          ~20 mi coverage
        </span>
      </div>
    </div>
  );
};

export default ServiceAreaMap;
